import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableService,
} from '@web/shared/ui';
import { Observable } from 'rxjs';
import { AdminBackgroundTaskCollectionItem } from '../../models/admin-background-task-collection-item';
import { AdminBackgroundTaskCollectionFilter } from '../../models/admin-background-task-collection-filter';
import { BackgroundTaskStatusProviderService, BackgroundTaskTypeProviderService } from '../../providers';
import { AdminBackgroundTaskListItemAction } from '../../enums/admin-background-task-list-item-action';
import { AdminBackgroundTaskDataSourceService } from '@web/features/system';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AdminBackgroundTaskListCustomCellTemplateComponent } from '../../components/admin-background-task-list-custom-cell-template/admin-background-task-list-custom-cell-template.component';

@Component({
  selector: 'app-admin-background-task-list',
  imports: [TableComponent, AdminBackgroundTaskListCustomCellTemplateComponent],
  templateUrl: './admin-background-task-list.component.html',
  styleUrl: './admin-background-task-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBackgroundTaskListComponent implements OnInit {
  protected config!: TableConfig<
    AdminBackgroundTaskCollectionItem,
    AdminBackgroundTaskCollectionFilter,
    AdminBackgroundTaskListItemAction
  >;

  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminBackgroundTaskCollectionItem, AdminBackgroundTaskCollectionFilter, AdminBackgroundTaskListItemAction>).create();
  private readonly backgroundTaskDataSource = inject(AdminBackgroundTaskDataSourceService);
  private readonly statusProvider = inject(BackgroundTaskStatusProviderService);
  private readonly typeProvider = inject(BackgroundTaskTypeProviderService);
  private readonly table = inject(TableService);

  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.buildTable();
  }

  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initCommand(AdminBackgroundTaskListItemAction.Delete)
          .icon(Icon.Trash)
          .tooltip('Usuń')
          .visibility((item) => item.canBeDeleted)
          .command((item) => this.confirmAndRemove(item))
          .build(),
      ])
      .filters((builder) => [
        builder
          .initMultiSelect('status')
          .dictionary(() => this.statusProvider.selectItems())
          .build(),

        builder
          .initMultiSelect('type')
          .dictionary(() => this.typeProvider.selectItems())
          .build(),

        builder.initDateRange('createdAt').build(),
        builder.initDateRange('updatedAt').build(),
      ])
      .columns((builder) => [
        builder
          .initCustom('status')
          .width('16rem')
          .label('Status')
          .sortKey('status')
          .filterKey('status')
          .build(),

        builder
          .initCustom('type')
          .width('22rem')
          .label('Rodzaj')
          .sortKey('type')
          .filterKey('type')
          .build(),

        builder
          .initDateTime('createdAt')
          .label('Data startu')
          .sortKey('createdAt')
          .filterKey('createdAt')
          .build(),

        builder
          .initDateTime('updatedAt')
          .label('Data ostatniej aktualizacji')
          .sortKey('updatedAt')
          .filterKey('updatedAt')
          .build(),

        builder
          .initCustom('progress')
          .width('18rem')
          .label('Postęp')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminBackgroundTaskCollectionFilter>,
        ): Observable<TableDataPayload<AdminBackgroundTaskCollectionItem>> =>
          this.backgroundTaskDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private confirmAndRemove(item: AdminBackgroundTaskCollectionItem): void {
    this.backgroundTaskDataSource
      .confirmAndRemoveById(item.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh());
  }

  private refresh(): void {
    this.table.refresh();
  }
}
